import React, { FC, useRef, MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { addProtocol } from '../../../utils/addProtocol';
import { cropPublicId } from '../../../utils/cropPublicId';
import { formatDistanceToNow } from '../../../utils/format/formatDistanceToNow';
import { formatText } from '../../../utils/format/formatText';
import { getWindow } from '../../../utils/getWindow';
import { useTranslation } from '../../../utils/useTranslation';
import { Skeleton } from '../../feedback/Skeleton';
import { IconBookmark } from '../../icon/IconBookmark';
import { IconBookmarkFilled } from '../../icon/IconBookmarkFilled';
import { IconDislike } from '../../icon/IconDislike';
import { IconDislikeFilled } from '../../icon/IconDislikeFilled';
import { IconFollow } from '../../icon/IconFollow';
import { IconLike } from '../../icon/IconLike';
import { IconNewTab } from '../../icon/IconNewTab';
import { IconShare } from '../../icon/IconShare';
import { IconUnfollowChecked } from '../../icon/IconUnfollowChecked';
import { Button } from '../../input/Button';
import { IconButton } from '../../input/IconButton';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { TranslateBox } from '../../layout/TranslateBox';
import { Avatar } from '../../media/Avatar';
import { PostUI, ReactionState } from '../types';
import {
    DESCRIPTION_INDENTION,
    TOP_WIDTH_WITHOUT_BUSINESS_NAME,
    TRANSITION_TIMING_KOEF,
} from './constants';
import { Footer, Description, Text, ButtonBox, BusinessName } from './styles';

const getDescriptionMaxHeight = (postElem: HTMLDivElement | null) =>
    (postElem?.offsetHeight || 0) - DESCRIPTION_INDENTION;

const getFirstParagraph = (str: string) => `${str.split('\n')[0]}...`;

const getTransitionTiming = (discriptionElem: HTMLDivElement | null) =>
    Math.log(discriptionElem?.offsetHeight || 0) * TRANSITION_TIMING_KOEF;

const makeTransition = ({ ms, styles }: { styles: string[]; ms: number }) =>
    styles.map((style) => `${style} ${ms}ms linear`).join(', ');

interface AnimateFooterProps {
    isExpanded: boolean;
    postElement: HTMLDivElement | null;
    post: PostUI;
    onSubscribe: (event: MouseEvent<HTMLElement>) => void;
    onReaction: (reactionState: PostUI['reactionState']) => void;
    onBookmark: (event: MouseEvent<HTMLElement>) => void;
    onExpand: (event: MouseEvent<HTMLElement>) => void;
    onClickOutClick: (event: MouseEvent<HTMLElement>) => void;
    onShare: () => void;
}

export const AnimateFooter: FC<AnimateFooterProps> = ({
    isExpanded,
    postElement,
    post,
    onClickOutClick,
    onSubscribe,
    onReaction,
    onBookmark,
    onExpand,
    onShare,
}) => {
    const descrRef = useRef<HTMLDivElement | null>(null);
    const button = (post.clickouts ?? []).find((c) => c.target === 'button' && c.url);
    const [businessNameWidth, setBusinessNameWidth] = useState<number | undefined>(undefined);
    const t = useTranslation();
    const transitionTiming = getTransitionTiming(descrRef.current);

    const fullBusinessNameEnabled =
        businessNameWidth! + TOP_WIDTH_WITHOUT_BUSINESS_NAME < getWindow()?.innerWidth!;

    return (
        // @ts-ignore
        <Footer
            isExpanded={isExpanded}
            descriptionHeight={descrRef.current?.offsetHeight || 0}
            transition={makeTransition({
                ms: transitionTiming,
                styles: ['margin-bottom', 'transform'],
            })}
        >
            <Box width="100%">
                <Box overflow="hidden" width="100%">
                    <Flex
                        pointerEvents="auto"
                        width="100%"
                        direction="row"
                        justifyContent="space-between"
                        alignItems="end"
                    >
                        <Flex mb={2} direction="row" alignItems="center" width="100%">
                            <Flex
                                as={Link}
                                to={post.business.url}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                textDecoration="none"
                            >
                                <Avatar
                                    width={34}
                                    title={post.business.displayName}
                                    src={post.business.avatarUrl}
                                    border="1px solid var(--divider)"
                                />
                                <BusinessName
                                    display="inline"
                                    isShort={isExpanded && !fullBusinessNameEnabled}
                                    businessNameWidth={businessNameWidth}
                                    ref={(node) => {
                                        node?.offsetWidth && setBusinessNameWidth(node.offsetWidth);
                                    }}
                                    transition={makeTransition({
                                        ms: transitionTiming,
                                        styles: ['max-width'],
                                    })}
                                >
                                    {cropPublicId(post.business.publicId)}
                                </BusinessName>
                            </Flex>
                            <Box ml={1}>
                                <IconButton onClick={onSubscribe} marginBottom={2}>
                                    {post.business.subscribed ? (
                                        <IconUnfollowChecked fontSize={24} color="#FFFFFF" />
                                    ) : (
                                        <IconFollow fontSize={24} color="#FFFFFF" />
                                    )}
                                </IconButton>
                            </Box>
                        </Flex>
                        <Flex direction="column" mb={2} transform="translateX(8px)">
                            <TranslateBox
                                trigger={isExpanded}
                                transition={makeTransition({
                                    styles: ['transform'],
                                    ms: transitionTiming,
                                })}
                                x2={-120}
                                y2={120}
                            >
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onReaction(ReactionState.LIKE);
                                    }}
                                >
                                    <IconLike
                                        fontSize={24}
                                        strokeWidth="2px"
                                        stroke="#FFFFFF"
                                        fill={
                                            post.reactionState === 'like'
                                                ? '#FFFFFF'
                                                : 'transparent'
                                        }
                                    />
                                </IconButton>
                            </TranslateBox>

                            <TranslateBox
                                trigger={isExpanded}
                                transition={makeTransition({
                                    styles: ['transform'],
                                    ms: transitionTiming,
                                })}
                                x2={-80}
                                y2={80}
                            >
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onReaction(ReactionState.DISLIKE);
                                    }}
                                >
                                    {post.reactionState === 'dislike' ? (
                                        <IconDislikeFilled
                                            fontSize={24}
                                            strokeWidth="2px"
                                            fill="#FFFFFF"
                                        />
                                    ) : (
                                        <IconDislike
                                            fontSize={24}
                                            strokeWidth="2px"
                                            fill="#FFFFFF"
                                        />
                                    )}
                                </IconButton>
                            </TranslateBox>

                            <TranslateBox
                                trigger={isExpanded}
                                transition={makeTransition({
                                    styles: ['transform'],
                                    ms: transitionTiming,
                                })}
                                x2={-40}
                                y2={40}
                            >
                                <IconButton onClick={onBookmark}>
                                    {post.saved ? (
                                        <IconBookmarkFilled fontSize={24} color="#FFFFFF" />
                                    ) : (
                                        <IconBookmark fontSize={24} color="#FFFFFF" />
                                    )}
                                </IconButton>
                            </TranslateBox>

                            <IconButton onClick={onShare}>
                                <IconShare color="#FFFFFF" />
                            </IconButton>
                        </Flex>
                    </Flex>
                </Box>
                <Box onClick={onExpand} cursor="pointer">
                    <Description
                        transition={makeTransition({ styles: ['opacity'], ms: transitionTiming })}
                        isExpanded={isExpanded}
                        type="short"
                    >
                        {getFirstParagraph(post.text)}
                    </Description>
                    <Description
                        ref={descrRef}
                        transition={makeTransition({ styles: ['opacity'], ms: transitionTiming })}
                        maxHeight={getDescriptionMaxHeight(postElement)}
                        isExpanded={isExpanded}
                        type="normal"
                    >
                        {formatText(post.text)}
                        <Text as="span" opacity={0.75} mt={2}>
                            {formatDistanceToNow(post.createdAt)}
                            {post.likes > 10 && (
                                <Box display="inline" as="span" ml={2}>
                                    {t('Понравилось: ')}
                                    {post.likes}
                                </Box>
                            )}
                        </Text>
                    </Description>
                </Box>
            </Box>
            {button && (
                <ButtonBox
                    isExpanded={isExpanded}
                    descriptionHeight={descrRef.current?.offsetHeight || 0}
                    transition={makeTransition({
                        styles: ['width', 'transform'],
                        ms: transitionTiming,
                    })}
                >
                    <Button
                        as="a"
                        target="_blank"
                        rel="nofollow noreferrer"
                        flexGrow={1}
                        href={addProtocol(button.url)}
                        color="hardLight"
                        onClick={onClickOutClick}
                        width="100%"
                        fontSize="1rem"
                    >
                        {button.text}
                        <Box ml={1}>
                            <IconNewTab fontSize={14} color="#000" />
                        </Box>
                    </Button>
                </ButtonBox>
            )}
        </Footer>
    );
};

export const AnimateFooterSkeleton = () => (
    <Flex p={1} direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <Flex>
            <Flex justifyContent="space-between" direction="row" alignItems="center">
                <Skeleton variant="circular" width={34} height={34} />
                <Skeleton variant="text" width={150} height={18} ml={1} />
            </Flex>
            <Skeleton variant="text" width={260} height={18} mt={2} />
        </Flex>
        <Flex justifyContent="space-between">
            <Flex position="absolute" bottom="5rem" right="1rem">
                <Skeleton variant="circular" width={34} height={34} mt={1.5} />
                <Skeleton variant="circular" width={34} height={34} mt={1.5} />
                <Skeleton variant="circular" width={34} height={34} mt={1.5} />
                <Skeleton variant="circular" width={34} height={34} mt={1.5} />
            </Flex>
            <Skeleton variant="rect" width="100px" borderRadius="999px" height={34} mt="3rem" />
        </Flex>
    </Flex>
);
